/**
 * Public landing page — Apple-style: generous whitespace, big SF-style display
 * type, full-bleed alternating bands, subtle scroll reveals.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ClipboardList, Sparkles, CalendarCheck } from 'lucide-react';
import HomepageChat from '../components/HomepageChat';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const fadeIn = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.9, ease: 'easeOut' } },
};

const PillButton: React.FC<{
  to: string;
  variant?: 'primary' | 'secondary';
  children: React.ReactNode;
}> = ({ to, variant = 'primary', children }) => {
  const base =
    'inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-medium transition-colors';
  const styles =
    variant === 'primary'
      ? 'bg-apple-blue text-white hover:bg-apple-blueHover'
      : 'border border-apple-divider text-apple-ink hover:bg-white';
  return (
    <Link to={to} className={`${base} ${styles}`}>
      {children}
    </Link>
  );
};

const TopBar: React.FC = () => (
  <header className="fixed inset-x-0 top-0 z-40 bg-apple-bg/80 backdrop-blur supports-[backdrop-filter]:bg-apple-bg/70">
    <div className="mx-auto flex h-12 max-w-6xl items-center justify-end px-6">
      <Link
        to="/signin"
        className="text-sm text-apple-subtext transition-colors hover:text-apple-ink"
      >
        Sign In
      </Link>
    </div>
  </header>
);

const Hero: React.FC = () => (
  <section className="relative flex min-h-screen items-center justify-center bg-apple-bg px-6 pt-20">
    <motion.div
      initial="hidden"
      animate="show"
      variants={fadeIn}
      className="mx-auto max-w-4xl text-center"
    >
      <motion.h1
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="text-5xl font-semibold tracking-tight text-apple-ink sm:text-display-2 md:text-display-1"
      >
        Capital, organized.
      </motion.h1>
      <motion.p
        variants={fadeUp}
        initial="hidden"
        animate="show"
        transition={{ delay: 0.15 }}
        className="mx-auto mt-6 max-w-2xl text-lg text-apple-subtext sm:text-subhead"
      >
        AI-powered intelligence connecting startups, projects, and investors.
      </motion.p>
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        transition={{ delay: 0.3 }}
        className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row"
      >
        <PillButton to="/connect" variant="primary">
          Get Started
        </PillButton>
        <PillButton to="/signin" variant="secondary">
          Sign In
        </PillButton>
      </motion.div>
    </motion.div>
  </section>
);

interface PillarProps {
  eyebrow: string;
  headline: string;
  body: string;
  dark?: boolean;
}

const Pillar: React.FC<PillarProps> = ({ eyebrow, headline, body, dark }) => (
  <section
    className={`flex min-h-screen items-center px-6 py-section ${
      dark ? 'bg-apple-black text-white' : 'bg-apple-bg text-apple-ink'
    }`}
  >
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.4 }}
      className="mx-auto max-w-4xl text-center"
    >
      <p
        className={`text-sm font-medium uppercase tracking-widest ${
          dark ? 'text-white/60' : 'text-apple-subtext'
        }`}
      >
        {eyebrow}
      </p>
      <h2 className="mt-4 text-4xl font-semibold tracking-tight sm:text-headline md:text-display-2">
        {headline}
      </h2>
      <p
        className={`mx-auto mt-6 max-w-2xl text-lg sm:text-subhead ${
          dark ? 'text-white/70' : 'text-apple-subtext'
        }`}
      >
        {body}
      </p>
    </motion.div>
  </section>
);

const HowItWorks: React.FC = () => {
  const steps = [
    {
      icon: ClipboardList,
      title: 'Tell us about you',
      body: 'Answer a few questions — takes about a minute.',
    },
    {
      icon: Sparkles,
      title: 'We score your fit',
      body: 'Our model matches you with the right next step.',
    },
    {
      icon: CalendarCheck,
      title: 'Meet Aaron',
      body: "Instantly if you're a strong match — otherwise, we'll be in touch.",
    },
  ];
  return (
    <section className="bg-apple-bg px-6 py-section">
      <div className="mx-auto max-w-6xl">
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.4 }}
          className="text-center text-4xl font-semibold tracking-tight text-apple-ink sm:text-headline"
        >
          How it works
        </motion.h2>
        <div className="mt-16 grid gap-10 md:grid-cols-3">
          {steps.map((s, i) => (
            <motion.div
              key={s.title}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.3 }}
              transition={{ delay: i * 0.1 }}
              className="rounded-3xl bg-white p-10 text-center shadow-card"
            >
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-apple-bg">
                <s.icon className="h-6 w-6 text-apple-blue" />
              </div>
              <h3 className="mt-6 text-xl font-semibold text-apple-ink">{s.title}</h3>
              <p className="mt-3 text-apple-subtext">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ClosingCTA: React.FC = () => (
  <section className="flex min-h-[60vh] items-center bg-apple-black px-6 py-section text-white">
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.4 }}
      className="mx-auto max-w-3xl text-center"
    >
      <h2 className="text-4xl font-semibold tracking-tight sm:text-display-2">Ready?</h2>
      <p className="mx-auto mt-6 max-w-xl text-lg text-white/70 sm:text-subhead">
        It takes about a minute. We'll meet you where you are.
      </p>
      <div className="mt-10">
        <PillButton to="/connect" variant="primary">
          Get Started
        </PillButton>
      </div>
    </motion.div>
  </section>
);

const Footer: React.FC = () => (
  <footer className="border-t border-apple-divider bg-apple-bg px-6 py-12 text-center text-sm text-apple-subtext">
    <p>&copy; {new Date().getFullYear()} Mule Works. All rights reserved.</p>
  </footer>
);

const LandingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-apple-bg font-sans antialiased">
      <TopBar />
      <main>
        <Hero />
        <Pillar
          eyebrow="For Investors"
          headline="See deals before the rest of the market."
          body="Curated, AI-screened opportunities aligned with your thesis — surfaced the moment they emerge, not weeks later."
          dark
        />
        <Pillar
          eyebrow="For Founders"
          headline="Get capital that actually fits."
          body="Skip the cold-outreach grind. We match your stage, sector, and check size to investors who are actively writing."
        />
        <Pillar
          eyebrow="For Strategic Buyers"
          headline="Buy smarter. Move faster."
          body="From sourcing through close, we organize the diligence so you can focus on the decisions that matter."
          dark
        />
        <HowItWorks />
        <ClosingCTA />
      </main>
      <Footer />
      <HomepageChat />
    </div>
  );
};

export default LandingPage;
